import { useAlert } from "matsuri-ui"
import { css } from "@emotion/react"
import { useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import dayjs from "dayjs"
import { useAuth } from "m2m-components/storage/useM2mAuth"
import { ConfirmStep } from "./ConfirmStep"
import { TransportationExpenseForm } from "./TransportationExpenseForm"
import { CleanerPageHeader } from "@/parts/CleanerPageHeader"
import {
    findExpenseOnDate,
    minSelectableExpenseDate
} from "@/model/transportationExpense"
import { extractPlacementName } from "@/model/placement"
import { DateString, formatDate } from "@/utils/dateString"
import { theme } from "@/theme"
import {
    transportationExpenseErrorMessages,
    useTransportationExpenseMutation,
    useTransportationExpensesForCleaner
} from "@/hooks/useTransportationExpense"
import { useSimpleCleaningsByDates } from "@/hooks/useSimpleCleaning"
import { usePlacementDetailsByOperationId } from "@/hooks/usePlacementDetailsByOperationId"
import { usePhotoTourByOperationIds } from "@/hooks/usePhotoTourByOperationId"
import { useTranslation } from "@/hooks/useTranslation"

interface ConfirmInput {
    amount: number
    routeDescription: string
}

export const NewTransportationExpensePage = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { throwAlert } = useAlert()
    const { token } = useAuth()

    const today: DateString = formatDate(dayjs())
    const [expenseDate, setExpenseDate] = useState<DateString>(today)
    const [amountRaw, setAmountRaw] = useState("")
    const [routeDescription, setRouteDescription] = useState("")
    const [confirmInput, setConfirmInput] = useState<ConfirmInput | undefined>(
        undefined
    )
    const [submitting, setSubmitting] = useState(false)

    const {
        data: expensesData,
        isLoading: isLoadingExpenses,
        refetch
    } = useTransportationExpensesForCleaner({
        // 重複チェックに必要なのは申請できる範囲（前日〜当日）だけ
        limit: 2,
        expenseDateFrom: minSelectableExpenseDate(today),
        expenseDateTo: today
    })
    const alreadyRegistered =
        findExpenseOnDate(
            expensesData?.transportationExpenses ?? [],
            expenseDate
        ) !== undefined

    // 報告済みの清掃も対象に含めるため、アサインベースのAPIで取得する
    const { data: cleaningsByDate, isLoading: isLoadingCleanings } =
        useSimpleCleaningsByDates([expenseDate])
    const cleaningIdsOnDate = useMemo(
        () =>
            (cleaningsByDate?.[expenseDate] ?? []).map(cleaning => cleaning.id),
        [cleaningsByDate, expenseDate]
    )
    const { data: placementDetails, isLoading: isLoadingPlacements } =
        usePlacementDetailsByOperationId(token, cleaningIdsOnDate)
    const { data: photoTourByCleaningId, isLoading: isLoadingPhotoTours } =
        usePhotoTourByOperationIds(token, cleaningIdsOnDate)
    const linkedCleaningNames = useMemo(
        () =>
            cleaningIdsOnDate
                .map(cleaningId => {
                    const detail = placementDetails?.[cleaningId]
                    if (!detail) return ""

                    const placementName = extractPlacementName(detail)
                    const tourTitle = photoTourByCleaningId?.[cleaningId]?.title

                    return tourTitle
                        ? `${placementName}（${tourTitle}）`
                        : placementName
                })
                .filter(name => name !== ""),
        [cleaningIdsOnDate, placementDetails, photoTourByCleaningId]
    )

    const { registerTransportationExpense } = useTransportationExpenseMutation()

    const handleSubmit = async () => {
        if (!confirmInput || submitting) {
            return
        }

        setSubmitting(true)
        try {
            const { error } = await registerTransportationExpense({
                expenseDate,
                amount: confirmInput.amount,
                routeDescription: confirmInput.routeDescription
            })

            const errorType = error?.parse?.()?.errorType
            if (errorType === "transportation_expense_already_registered") {
                // 一覧を再取得して申請済み表示に同期する
                refetch()
                setConfirmInput(undefined)
            }

            throwAlert(error, {
                errorMessage: {
                    happend: t(
                        (errorType &&
                            transportationExpenseErrorMessages[errorType]) ||
                            "交通費の申請に失敗しました"
                    )
                },
                successMessage: t("交通費を申請しました"),
                onSuccess: () => {
                    refetch()
                    navigate("/transportation_expenses")
                }
            })
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div
            css={css`
                min-height: 100vh;
                background-color: ${
                    theme.palette.intentions.cleanerBackground.main
                };
            `}
        >
            <CleanerPageHeader
                title={t("新規申請")}
                onPrev={() => {
                    if (confirmInput) {
                        setConfirmInput(undefined)
                        return
                    }
                    navigate("/transportation_expenses")
                }}
            />

            <div
                css={css`
                    padding: 16px 20px;
                `}
            >
                {confirmInput ? (
                    <ConfirmStep
                        expenseDate={expenseDate}
                        amount={confirmInput.amount}
                        routeDescription={confirmInput.routeDescription}
                        submitting={submitting}
                        onSubmit={handleSubmit}
                        onBack={() => setConfirmInput(undefined)}
                    />
                ) : (
                    <TransportationExpenseForm
                        expenseDate={expenseDate}
                        onChangeExpenseDate={setExpenseDate}
                        linkedCleaningNames={linkedCleaningNames}
                        hasCleaningsOnDate={cleaningIdsOnDate.length > 0}
                        isLoadingCleanings={
                            isLoadingCleanings ||
                            isLoadingPlacements ||
                            isLoadingPhotoTours
                        }
                        isLoadingExpenses={isLoadingExpenses}
                        alreadyRegistered={alreadyRegistered}
                        amountRaw={amountRaw}
                        onChangeAmountRaw={setAmountRaw}
                        routeDescription={routeDescription}
                        onChangeRouteDescription={setRouteDescription}
                        onNext={setConfirmInput}
                    />
                )}
            </div>
        </div>
    )
}
